import React, { useState, useEffect, useCallback } from 'react';

const fmt = (n) => Number(n || 0).toFixed(2);

const PAYMENT_LABELS = {
  cash:     { label: 'Gotówka',  icon: '💵' },
  card:     { label: 'Karta',    icon: '💳' },
  blik:     { label: 'BLIK',     icon: '📱' },
  transfer: { label: 'Przelew',  icon: '🏦' },
  receipt:  { label: 'Paragon',  icon: '🧾' },
};

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function CashToday() {
  const [date, setDate]       = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState('');
  const [expanded, setExpanded] = useState(new Set());

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async (d) => {
    try {
      const r = await fetch(`/api/cash/today?date=${d}`, { headers: authHeaders() });
      const json = await r.json();
      setData(json);
    } catch { showToast('Błąd ładowania danych'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setLoading(true);
    load(date);
    const isToday = date === todayStr();
    if (!isToday) return;
    const interval = setInterval(() => load(date), 15000);
    return () => clearInterval(interval);
  }, [date, load]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie...</div>;

  const reports = data?.reports || [];
  const totals  = data?.totals  || {};
  const openCount = data?.open_count || 0;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>📒 Sprzedaż dnia</h2>
        {openCount > 0 && date === todayStr() && (
          <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#fffbeb', color: '#854F0B', fontWeight: 500 }}>
            {openCount} {openCount === 1 ? 'stanowisko otwarte' : 'stanowiska otwarte'}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input type="date" value={date} max={todayStr()} onChange={e => setDate(e.target.value)}
          style={{ padding: '7px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }} />
        <button onClick={() => setDate(todayStr())} className="btn-sm">Dziś</button>
        <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 1); setDate(d.toISOString().split('T')[0]); }} className="btn-sm">Wczoraj</button>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {new Date(date).toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          {date === todayStr() ? ' · odświeża się automatycznie' : ''}
        </span>
      </div>

      {/* Podsumowanie zbiorcze */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 20, marginBottom: 16, border: '0.5px solid var(--border)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Transakcji</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{totals.orders || 0}</div>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Utarg łącznie</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(totals.total)} zł</div>
          </div>
        </div>

        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 10 }}>Wg formy płatności</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {Object.entries(PAYMENT_LABELS).map(([key, m]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{m.icon} {m.label}</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totals[key])} zł</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Raporty kasowe per kasjer */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', fontSize: 13, fontWeight: 500, borderBottom: '0.5px solid var(--border)' }}>
          Raporty kasowe — kasjerzy
        </div>
        {reports.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Brak raportów dzisiaj</div>
        ) : reports.map((r, i) => {
          const isOpen = r.status === 'open';
          const isExpanded = expanded.has(r.id);
          const initials = (r.user_name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
          const hasOps = (r.cash_in > 0) || (r.cash_out > 0);
          const timeFmt = t => new Date(t).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
          return (
            <div key={r.id} style={{ borderBottom: i < reports.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
              <div onClick={() => toggleExpand(r.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                background: isOpen ? '#eff6ff' : 'transparent', cursor: 'pointer',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: isOpen ? '#dbeafe' : '#f0fdf4',
                  color: isOpen ? '#185FA5' : '#16a34a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{r.user_name}</div>
                  <div style={{ fontSize: 11, color: isOpen ? '#185FA5' : 'var(--text-muted)' }}>
                    Otwarty {timeFmt(r.opened_at)}
                    {!isOpen && r.closed_at && ` · Zamknięty ${timeFmt(r.closed_at)}`}
                    {' · '}{r.order_count} transakcji
                  </div>
                  {hasOps && (
                    <div style={{ fontSize: 11, marginTop: 2, display: 'flex', gap: 10 }}>
                      {r.cash_in > 0 && <span style={{ color: '#16a34a' }}>🟢 Wpłaty: {fmt(r.cash_in)} zł</span>}
                      {r.cash_out > 0 && <span style={{ color: '#dc2626' }}>🔴 Wypłaty: {fmt(r.cash_out)} zł</span>}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(r.total_sales)} zł</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>💵 {fmt(r.cash_in_drawer)} zł</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {isExpanded && (
                <div style={{ padding: '0 16px 14px 60px', background: isOpen ? '#eff6ff' : 'transparent' }}>
                  {(r.operations || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Brak wpłat/wypłat w tym raporcie</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {r.operations.map(op => (
                        <div key={op.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg)', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                          <span>
                            {op.type === 'in' ? '🟢' : '🔴'} {timeFmt(op.created_at)}
                            {op.added_by && <span style={{ color: 'var(--text-muted)' }}> · {op.added_by}</span>}
                            {op.description && <span style={{ color: 'var(--text-muted)' }}> · {op.description}</span>}
                          </span>
                          <span style={{ fontWeight: 600, color: op.type === 'in' ? '#16a34a' : '#dc2626' }}>
                            {op.type === 'in' ? '+' : '-'}{fmt(op.amount)} zł
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14, textAlign: 'center' }}>
        Suma liczona na bieżąco — nie wymaga zamykania zmiany. Każdy kasjer zamyka swój raport indywidualnie w zakładce „Kasa”.
      </div>
    </div>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#fff', padding: '10px 20px', borderRadius: 8, zIndex: 999, fontSize: 14 }}>
      {msg}
    </div>
  );
}

function authHeaders() {
  const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
  return { Authorization: `Bearer ${token}` };
}
